/**
 * isiZulu: Contacts
 *
 * What this file covers: The contact book: contacts, fields, tags, segments, imports, consent and erasing a
 * person's data.
 *
 * Tone: Clear and careful. Consent and erasure wording is legal in effect: say exactly what happens to the
 * person's data.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const contacts: Catalog = {
  'contacts.title': 'Oxhumana nabo',
  'contacts.optedIn': 'Uvumile',
  'contacts.optedOut': 'Uhoxisile',
  'contacts.actions.importCsv': 'Ngenisa i-CSV',
  'contacts.loadError': 'Ayikwazanga ukulayisha oxhumana nabo.',
  'contacts.shown': '{{count}} kuboniswe',
  'contacts.columns.optIn': 'Ukuvuma',
  'contacts.optIn.in': 'Uvumile',
  'contacts.optIn.out': 'Uhoxisile',
  'contacts.modal.delete.title': 'Susa oxhumana naye?',
  'contacts.toast.importQueuedTitle': 'Ukungenisa i-CSV',
  'contacts.toast.importFailed': 'Ukungenisa kwehlulekile',
  'contacts.drawer.new': 'Oxhumana naye omusha',
  'contacts.drawer.edit': 'Hlela oxhumana naye',
  'contacts.form.tagsHint': 'Cindezela u-Enter ngemuva kwethegi ngayinye.',
  'contacts.form.optIn': 'Uvumile',
  'contacts.view.notFound': 'Oxhumana naye akatholakali.',
  'contacts.view.header.noOptIn': 'akukho ukuvuma',
  'contacts.view.backToAll': 'Bonke oxhumana nabo',
  'contacts.view.tabs.profile': 'Iphrofayela',
  'contacts.view.tabs.conversations': 'Izingxoxo',
  'contacts.view.tabs.danger': 'Ingozi',
  'contacts.view.profile.locale': 'Indawo',
  'contacts.view.profile.contactId': 'I-ID yoxhumana naye',
  'contacts.view.tags.empty': 'Awekho amalebula okwamanje.',
  'contacts.view.conversations.empty': 'Azikho izingxoxo nalo oxhumana naye.',
  'contacts.view.conversations.noPreview': '(akukho ukubuka kuqala)',
  'contacts.view.danger.markOptOut': 'Maka njengohoxisile',
  'contacts.view.danger.markOptIn': 'Maka njengovumile',
  'contacts.view.danger.deleteContact': 'Susa oxhumana naye',
  'contacts.view.danger.modal.delete.irreversible': 'Lokhu ngeke kuhlehliswe.',
  'contacts.view.danger.toast.deleteFailed': 'Ayikwazanga ukususa oxhumana naye.',
  'contacts.actions.import': 'Ngenisa',
  'contacts.actions.create': 'Dala othintana naye',
  'contacts.export.csv': 'Khipha njenge-CSV',
  'contacts.export.xlsx': 'Khipha njenge-Excel (.xlsx)',
  'contacts.columns.contact': 'Othintana naye',
  'contacts.filters.clear': 'Sula izihlungi',
  'contacts.filters.tag.option': '{{tag}} ({{count}})',
  'contacts.rowsPerPage': 'Imigqa ekhasini ngalinye',
  'contacts.form.tab.details': 'Imininingwane',
  'contacts.form.tab.notes': 'Amanothi',
  'contacts.form.fullName': 'Igama eligcwele',
  'contacts.form.phone': 'Inombolo yefoni',
  'contacts.form.save': 'Londoloza othintana naye',
  'contacts.bulk.count': '{{count}} okukhethiwe',
  'contacts.bulk.add': 'Engeza ithegi',
  'contacts.bulk.remove': 'Susa ithegi',
  'contacts.bulk.clear': 'Sula okukhethiwe',
  'contacts.tags.add': 'Engeza ithegi',
  'contacts.showingOf': 'Kukhonjiswa {{count}} kwabangu-{{total}}',
  'contacts.filters.title': 'Izihlungi',
  'contacts.drawer.view': 'Othintana naye',
  'contacts.actions.message': 'Thumela umyalezo',
  'contacts.bulk.message': 'Thumela umyalezo',
  'contacts.fields.title': 'Izinkambu zoxhumana naye',
  'contacts.fields.description': 'Imininingwane eyengeziwe oyigcina ngalowo oxhumana naye ngaphandle kwegama, inombolo ne-imeyili. Inkambu ivela efomini loxhumana naye, kusihlungi sohlu nasezingxenyeni, futhi ihamba nokungenisa nokukhipha.',
  'contacts.fields.new': 'Inkambu entsha',
  'contacts.fields.edit': 'Hlela inkambu',
  'contacts.fields.empty': 'Azikho izinkambu ezenziwe ngokwezifiso okwamanje',
  'contacts.fields.emptyHint': 'Engeza inkambu yanoma yini okufanele igcinwe ngomuntu: inombolo ye-oda, uhlelo, usuku lokuvuselela.',
  'contacts.fields.loadError': 'Ayikwazanga ukulayisha izinkambu zoxhumana naye.',
  'contacts.fields.columns.label': 'Ilebula',
  'contacts.fields.columns.key': 'Ukhiye',
  'contacts.fields.columns.type': 'Uhlobo',
  'contacts.fields.columns.usage': 'Ukusetshenziswa',
  'contacts.fields.columns.active': 'Isimo',
  'contacts.fields.usage': '{{count}} abaxhumana nabo',
  'contacts.fields.usage.one': '1 oxhumana naye',
  'contacts.fields.active': 'Iyasebenza',
  'contacts.fields.inactive': 'Ifihliwe',
  'contacts.fields.type.text': 'Umbhalo',
  'contacts.fields.type.number': 'Inombolo',
  'contacts.fields.type.date': 'Usuku',
  'contacts.fields.type.boolean': 'Yebo / cha',
  'contacts.fields.type.select': 'Ukukhetha',
  'contacts.fields.type.url': 'Ikheli lewebhu',
  'contacts.fields.form.label': 'Ilebula',
  'contacts.fields.form.labelHint': 'Lokho inkambu ebizwa ngakho efomini loxhumana naye.',
  'contacts.fields.form.key': 'Ukhiye',
  'contacts.fields.form.keyHint': 'Ulandela ilebula uze uyishintshe: izinhlamvu ezincane, izinombolo nemigqa engaphansi. Uqamba ikholomu ekungeniseni nasekukhipheni kanye nendawo yokubamba emilayezweni.',
  'contacts.fields.form.keyLocked': 'Ukhiye awukwazi ukushintsha uma inkambu isikhona: lonke inani eligcinwe koxhumana naye lifakwe ngaphansi kwawo.',
  'contacts.fields.form.type': 'Uhlobo',
  'contacts.fields.form.typeHint': 'Indlela inani eligcinwa ngayo, nendlela izihlungi nezingxenye eziliqhathanisa ngayo.',
  'contacts.fields.form.typeLocked': 'Uhlobo alukwazi ukushintsha uma inkambu isikhona: amanani asegciniwe abezoyeka ukusho lokho abekusho.',
  'contacts.fields.form.options': 'Okukhethwayo',
  'contacts.fields.form.optionsHint': 'Okukodwa ngomugqa. Oxhumana naye ubamba okukodwa kuphela kulokhu.',
  'contacts.fields.form.optionsRequired': 'Inkambu yokukhetha idinga okungenani okukodwa okukhethwayo.',
  'contacts.fields.form.description': 'Incazelo',
  'contacts.fields.form.descriptionHint': 'Iboniswa ngaphansi kwenkambu efomini loxhumana naye.',
  'contacts.fields.form.active': 'Iyasebenza',
  'contacts.fields.form.activeHint': 'Ukucisha inkambu kuyifihla efomini loxhumana naye nasekukhipheni. Akukho okugciniwe kunoma yimuphi oxhumana naye okuthintwayo.',
  'contacts.fields.form.keyInvalid': 'Sebenzisa izinhlamvu ezincane, izinombolo nemigqa engaphansi, uqale ngohlamvu.',
  'contacts.fields.form.keyReserved': 'Wonke oxhumana naye usenayo inkambu enaleli gama.',
  'contacts.fields.form.keyTaken': 'Inkambu enalo khiye isivele ikhona.',
  'contacts.fields.toast.created': 'Inkambu “{{label}}” yengeziwe',
  'contacts.fields.toast.updated': 'Inkambu “{{label}}” igciniwe',
  'contacts.fields.toast.deleted': 'Inkambu “{{label}}” isusiwe',
  'contacts.fields.toast.saveFailed': 'Ayikwazanga ukugcina inkambu',
  'contacts.fields.toast.deleteFailed': 'Ayikwazanga ukususa inkambu',
  'contacts.fields.delete.title': 'Susa “{{label}}”?',
  'contacts.fields.delete.body': '{{count}} abaxhumana nabo banenani lale nkambu. Ukuyisusa kucima lawo manani kubo bonke. Lokhu akukwazi ukubuyiselwa.',
  'contacts.fields.delete.bodyNone': 'Akekho oxhumana naye onenani lale nkambu okwamanje.',
  'contacts.fields.delete.hint': 'Ukufihla inkambu ugcine amanani, yicishe kunalokho.',
  'contacts.fields.delete.confirm': 'Susa inkambu',
  'contacts.form.tab.fields': 'Izinkambu',
  'contacts.form.fields.empty': 'Azikho izinkambu ezenziwe ngokwezifiso okwamanje',
  'contacts.form.fields.emptyHint': 'Chaza imininingwane eyengeziwe oyigcina ngalowo oxhumana naye bese ivela lapha.',
  'contacts.form.fields.manage': 'Setha izinkambu zoxhumana naye',
  'contacts.form.fields.manageHint': 'Kushoda inkambu?',
  'contacts.form.fields.unset': 'Akusethiwe',
  'contacts.form.fields.loadError': 'Ayikwazanga ukulayisha izinkambu zoxhumana naye.',
  'contacts.form.fields.datePlaceholder': 'YYYY-MM-DD',
  'contacts.form.fields.urlPlaceholder': 'https://',
  'contacts.form.fields.invalid.number': 'Faka inombolo.',
  'contacts.form.fields.invalid.date': 'Faka usuku njenge-YYYY-MM-DD.',
  'contacts.form.fields.invalid.url': 'Faka ikheli lewebhu eliphelele, eliqala ngo-http:// noma https://.',
  'contacts.form.fields.invalid.select': 'Khetha okukodwa kokukhethwayo.',
  'contacts.form.fields.invalid.boolean': 'Khetha yebo noma cha.',
  'contacts.form.fields.invalid.unknown': 'Le nkambu ayisekho. Layisha kabusha uzame futhi.',
  'contacts.filters.field': 'Inkambu',
  'contacts.filters.field.any': 'Noma iyiphi inkambu',
  'contacts.filters.field.value': 'Inani',
  'contacts.filters.field.valueAny': 'Noma iliphi inani',
  'contacts.filters.field.yes': 'Yebo',
  'contacts.filters.field.no': 'Cha',
  'contacts.filters.field.placeholder': 'Thayipha inani',
  'contacts.import.preview.mapping': 'Amakholomu',
  'contacts.import.preview.column': 'Efayelini',
  'contacts.import.preview.target': 'Iya ku',
  'contacts.import.preview.target.ignored': 'Ishaywe indiva',
  'contacts.import.preview.target.unknown': 'Ayifani nalutho',
  'contacts.import.preview.target.field': 'Inkambu: {{label}}',
  'contacts.import.preview.createFields': 'Dala amakholomu angu-{{count}} angafani nalutho njengezinkambu zombhalo',
  'contacts.import.preview.createFieldsHint': 'Ngayinye iba yinkambu eyenziwe ngokwezifiso eqanjwe ngesihloko sayo, futhi amanani ayo angeniswa nokunye. Yishiye icishiwe ukuze weqe lawo makholomu.',
  'contacts.import.preview.fieldProblems': '{{count}} amanani awahambisani nohlobo lwenkambu yawo. Azogcinwa njengombhalo.',
  'contacts.import.preview.fieldProblem': 'Umugqa {{row}}, {{column}}: “{{value}}”',
  'contacts.import.doneFieldProblems': '{{count}} amanani awahambisananga nohlobo lwenkambu yawo futhi agcinwe njengombhalo.',
  'contacts.import.format.customFields': 'Noma iyiphi enye ikholomu ihlanganiswa nenkambu eyenziwe ngokwezifiso ngokhiye wayo noma ilebula yayo. Amakholomu angafani nalutho angadalwa njengezinkambu zombhalo kusuka ekubukeni kuqala.',
  'contacts.import.csv.col.notes': 'Amanothi ombhalo okhululekile agcinwe ngoxhumana naye.',
  'contacts.import.format.intro': 'Umugqa wokuqala kufanele ube isihloko esiqamba ikholomu ngayinye. Lawa ngamakholomu umngenisi awaziyo; noma isiphi esinye isihloko sihlanganiswa nenkambu eyenziwe ngokwezifiso.',
  'contacts.importCard.columns': 'Amakholomu: msisdn, name, email, locale, tags, notes, kanye nelilodwa ngenkambu eyenziwe ngokwezifiso ngayinye. Hlukanisa amathegi ngesemikholoni.'
};

export default contacts;
